/**
 * Credential resolution and storage state download for session commands.
 *
 * Uses shared selectCredential from cli-helpers for interactive selection,
 * then downloads the storage state for the selected credential.
 *
 * @module
 */

import { resolveConfig } from '../auth.js';
import { selectCredential, downloadStorageState } from '../cli-helpers.js';

/**
 * Resolve a credential and download its storage state.
 *
 * @param argv - CLI arguments (for --env, --token, --api-url resolution)
 * @param credentialArg - Credential name, ID, or undefined for interactive selection
 * @returns Object with storageStatePath and credentialName, or null if no match
 */
export async function resolveCredential(
  argv: string[],
  credentialArg: string | undefined
): Promise<{ storageStatePath: string | undefined; credentialName: string } | null> {
  const { apiUrl, token } = await resolveConfig(argv);

  const credential = await selectCredential(apiUrl, token, credentialArg);
  if (!credential) {
    return null;
  }

  console.log(`Fetching credential "${credential.name}"...`);

  let storageStatePath: string | undefined;
  if (credential.storageStateS3Key) {
    storageStatePath = await downloadStorageState({
      apiUrl,
      token,
      propertyId: credential.propertyId,
      credentialId: credential.id,
      prefix: 'session-ss',
    });

    if (storageStatePath) {
      console.log('Downloaded storage state.');
    } else {
      console.warn('Warning: Could not download storage state. Starting without auth.');
    }
  } else {
    console.log('Credential has no storage state. Starting without auth.');
  }

  return {
    storageStatePath,
    credentialName: credential.name,
  };
}
